import { useCallback, useMemo, useState } from 'react';

import { SurveyTemplate } from '@dreryk/egabinet-survey-frontend-api';

import { useFormController } from '@dreryk/react-common';
import {
   Accordion,
   ButtonVariant,
   componentsTranslationKeys,
   FormRouteLeavingPrompt,
   Heading,
   StyledFlex,
} from '@dreryk/react-components';

import { generalSettingsTranslationKeys } from '../../../../translations';

import { SurveyPreInterviewTemplateInteractiveTable } from './interactiveTable/SurveyPreInterviewTemplateInteractiveTable';
import {
   PreInterviewTemplateStatus,
   SurveyPreInterviewTemplateStatus,
} from './status/SurveyPreInterviewTemplateStatus';
import {
   SurveyPreInterviewTemplateFormCancelButton,
   SurveyPreInterviewTemplateFormDiscardChangesButton,
   SurveyPreInterviewTemplateFormEditButton,
   SurveyPreInterviewTemplateFormPublishButton,
   SurveyPreInterviewTemplateFormSaveAndPublishButton,
   SurveyPreInterviewTemplateFormSaveButton,
} from './SurveyPreInterviewTemplateForm.buttons';
import { SurveyPreInterviewTemplateFormBean } from './SurveyPreInterviewTemplateForm.types';
import {
   formInitializer,
   getInitialPreInterviewStatus,
   isEditMode,
   surveyPreInterviewTemplateFormButtonsForTemplateStatusVisitor,
} from './SurveyPreInterviewTemplateForm.utils';
import { SurveyPreInterviewTemplateVersion } from './version/SurveyPreInterviewTemplateVersion';

type SurveyPreInterviewTemplateFormProps = Readonly<{
   refetchSurveyTemplate: VoidFunction;
   surveyTemplate: SurveyTemplate;
}>;

// eslint-disable-next-line max-lines-per-function
export const SurveyPreInterviewTemplateForm = (props: SurveyPreInterviewTemplateFormProps): JSX.Element => {
   const defaultStatusByPublicationDate = useMemo(() => {
      return getInitialPreInterviewStatus(props.surveyTemplate.publicationDate);
   }, [props.surveyTemplate.publicationDate]);

   const [status, setStatus] = useState<PreInterviewTemplateStatus>(defaultStatusByPublicationDate);
   const { formState, handleSubmit, register, reset } = useFormController<SurveyPreInterviewTemplateFormBean>(
      useMemo(() => formInitializer(props.surveyTemplate), [props.surveyTemplate])
   );

   const changeStatus = useCallback(
      (preInterviewTemplateStatus: PreInterviewTemplateStatus) => () => {
         setStatus(preInterviewTemplateStatus);
         reset();
      },
      [reset]
   );

   return (
      <>
         <StyledFlex as={'form'} noValidate autoComplete={'off'} gap={8} direction={'column'}>
            <StyledFlex justifyContent={'space-between'}>
               <StyledFlex gap={8} alignItems={'center'}>
                  <Heading
                     type={'h3'}
                     testId={'header'}
                     content={generalSettingsTranslationKeys.survey.preInterview.header}
                     margin={0}
                  />
                  <SurveyPreInterviewTemplateStatus preInterviewTemplateStatus={status} />
                  <SurveyPreInterviewTemplateVersion
                     preInterviewTemplateStatus={status}
                     publicationDate={props.surveyTemplate.publicationDate}
                     defaultTemplate={props.surveyTemplate.defaultTemplate}
                     lastUpdateDate={props.surveyTemplate.lastUpdateDate}
                  />
               </StyledFlex>
               <StyledFlex gap={10}>
                  {surveyPreInterviewTemplateFormButtonsForTemplateStatusVisitor(status)({
                     visitPublished: () => (
                        <SurveyPreInterviewTemplateFormEditButton
                           onEditClick={changeStatus('EDIT')}
                           variant={ButtonVariant.PRIMARY}
                        />
                     ),
                     visitNotPublished: () => (
                        <>
                           <SurveyPreInterviewTemplateFormEditButton
                              onEditClick={changeStatus('EDIT')}
                              variant={ButtonVariant.SECONDARY}
                           />
                           <SurveyPreInterviewTemplateFormDiscardChangesButton
                              onSuccess={props.refetchSurveyTemplate}
                              preInterviewTemplateId={props.surveyTemplate.templateVersionId}
                           />
                           <SurveyPreInterviewTemplateFormPublishButton
                              onSuccess={props.refetchSurveyTemplate}
                              preInterviewTemplateId={props.surveyTemplate.templateVersionId}
                           />
                        </>
                     ),
                     visitEdit: () => (
                        <>
                           <SurveyPreInterviewTemplateFormCancelButton
                              reset={reset}
                              onCancel={changeStatus(defaultStatusByPublicationDate)}
                           />
                           <SurveyPreInterviewTemplateFormSaveButton
                              handleSubmit={handleSubmit}
                              onSuccess={props.refetchSurveyTemplate}
                           />
                           <SurveyPreInterviewTemplateFormSaveAndPublishButton
                              handleSubmit={handleSubmit}
                              onSuccess={props.refetchSurveyTemplate}
                           />
                        </>
                     ),
                  })}
               </StyledFlex>
            </StyledFlex>
            <SurveyPreInterviewTemplateInteractiveTable editMode={isEditMode(status)} register={register} />
         </StyledFlex>
         <FormRouteLeavingPrompt formState={formState} message={componentsTranslationKeys.formLeavingConfirmation} />
         <Accordion.Section.ClosingConfirmation
            sectionId={'survey'}
            shouldConfirm={formState.isDirty}
            content={componentsTranslationKeys.formLeavingConfirmation}
            title={componentsTranslationKeys.leaveForm}
            confirmationButtonLabel={componentsTranslationKeys.leave}
         />
      </>
   );
};
